/**
 * Singleton Socket.io client.
 * Connects to the /realtime namespace with JWT auth.
 * Call connect(token) on login, disconnect() on logout.
 */
import { io, Socket } from 'socket.io-client';

let socket: Socket | null = null;
let currentToken: string | null = null;
const connectedListeners = new Set<(connected: boolean) => void>();
let lastConnectedState = false;

function notifyConnected(state: boolean) {
  if (state === lastConnectedState) return;
  lastConnectedState = state;
  connectedListeners.forEach((cb) => {
    try { cb(state); } catch { /* ignore */ }
  });
}

export function subscribeSocketConnected(cb: (connected: boolean) => void): () => void {
  connectedListeners.add(cb);
  // Emit current state on subscribe
  cb(lastConnectedState);
  return () => { connectedListeners.delete(cb); };
}

export function isSocketConnected(): boolean {
  return lastConnectedState;
}

// In dev, connect directly to NestJS backend (bypasses Vite SPA fallback which intercepts /socket.io).
// In production, use same origin (nginx routes socket.io traffic to backend).
function getServerUrl(): string {
  if (typeof window === 'undefined') return 'http://localhost:3000';
  const { hostname, port } = window.location;
  const isViteDev = port === '5173' || port === '5174' || port === '5175';
  if (isViteDev) return `http://${hostname}:3000`;
  return window.location.origin;
}

function buildSocket(token: string): Socket {
  return io(`${getServerUrl()}/realtime`, {
    auth: { token },
    transports: ['websocket'],
    autoConnect: true,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
  });
}

export function connectSocket(token: string): Socket {
  if (socket && currentToken !== token) {
    socket.disconnect();
    socket = null;
  }
  if (!socket) {
    currentToken = token;
    socket = buildSocket(token);
    socket.on('connect', () => {
      console.debug('[socket] connected', socket?.id);
      notifyConnected(true);
    });
    socket.on('disconnect', (reason) => {
      console.debug('[socket] disconnected', reason);
      notifyConnected(false);
    });
    socket.on('connect_error', (err) => {
      console.warn('[socket] connect error', err.message);
      notifyConnected(false);
    });
  }
  return socket;
}

export function disconnectSocket(): void {
  socket?.disconnect();
  socket = null;
  currentToken = null;
  notifyConnected(false);
}

export function getSocket(): Socket | null {
  return socket;
}
